import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BlockerFunction, useBlocker } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

type ConfirmNavigationProps = {
  isBlocked: boolean;
  title?: string;
};

const ConfirmNavigation = ({ isBlocked, title }: ConfirmNavigationProps) => {
  const { t } = useTranslation();
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      isBlocked &&
      currentLocation.pathname.split("?")[0] !==
        nextLocation.pathname.split("?")[0],
    [isBlocked],
  );
  const blocker = useBlocker(shouldBlock);
  const isBlockedState = blocker.state === "blocked";
  const isProceedingState = blocker.state === "proceeding";

  const titleMemo = useMemo(
    () => title ?? t("confirmUserFormNotSaved"),
    [t, title],
  );

  const handleRouteChange = useCallback(
    (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = titleMemo;
      return titleMemo;
    },
    [titleMemo],
  );

  useEffect(() => {
    if (isBlocked) {
      window.addEventListener("beforeunload", handleRouteChange);
    } else {
      window.removeEventListener("beforeunload", handleRouteChange);
    }

    return () => {
      window.removeEventListener("beforeunload", handleRouteChange);
    };
  }, [isBlocked, handleRouteChange]);

  const handleSubmit = () => blocker.proceed?.();
  const handleCancel = () => blocker.reset?.();

  return (
    <AlertDialog
      open={isBlockedState || isProceedingState}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{titleMemo}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            {t("formCancel")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleSubmit}>
            {t("formOk")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmNavigation;
